import { Order } from "@amzn/ito-client";
import { Col, Flex, Spacer, View } from "@amzn/stencil-react-components/layout";
import { Spinner, SpinnerSize } from "@amzn/stencil-react-components/spinner";
import { RowAttributesFunction, Table, TableSpacing } from "@amzn/stencil-react-components/table";
import { Text } from "@amzn/stencil-react-components/text";
import { buildColumnDefinitions } from "common/helpers/table-utils";
import { ITOConsumers } from "common/types";
import { ColumnSetting } from "common/types/config-model";
import React, { FC } from "react";

import { AssetDetail } from "./AssetDetail";
import myOrdersTableConfig from "./my-software-orders.table-config.json";
import { useOrdersTable } from "./useOrdersPageTable";

export const MySoftwareOrdersTable: FC = () => {
  const hash = window.location.hash.replace("#", "");
  const columns: ColumnSetting[] = myOrdersTableConfig.columns;
  const currentColumnDefinitions = buildColumnDefinitions(columns);

  const { orders, isLoading } = useOrdersTable(ITOConsumers.ITMP);

  /* istanbul ignore next */
  const expandableContent = ({ data, index }: any) => {
    const order: Order = data;
    const content = buildContent(order, index);

    return content;
  };

  /* eslint-disable @typescript-eslint/no-unused-vars */
  const getRowAttributes: RowAttributesFunction = ({ data }: any) => ({
    expandableContent: expandableContent,
    disabled: false,
    id: data.orderId,
    isDefaultExpanded: data.orderId === hash,
  });

  if (isLoading) {
    return (
      <Flex flexDirection="column">
        <View alignSelf="center">
          <Spacer height={32} width={32} />
          <Spinner size={SpinnerSize.Medium} showText loadingText="Loading your orders" />
        </View>
      </Flex>
    );
  }

  return (
    <Col gridGap={32}>
      <Table
        actionHeader="Detail"
        columns={currentColumnDefinitions}
        data={orders}
        spacing={TableSpacing.Default}
        getRowAttributes={getRowAttributes}
      />
    </Col>
  );
};

/**
 * Builds the content for the expanded row based on the status of the order
 * @param data
 * @param index
 * @returns
 */
/* istanbul ignore next */
const buildContent = (data: any, index: number): React.JSX.Element[] => {
  const order: Order = data;
  let message: string | undefined;
  const innerContent: React.JSX.Element[] = [];
  switch (order.status) {
    case "APPROVED":
      message = "You order has been approved. The IT Shop team will complete your order shortly.";
      break;
    case "CANCELLED":
      message = order.orderLabels?.find((label) => label.description)?.description ?? "Your order has been cancelled.";
      break;
    case "COMPLETED":
      // will iterate on all assets, to display info for each
      if (order.assets) {
        for (const asset of order.assets) {
          innerContent.push(<AssetDetail asset={asset} order={order} />);
        }
      }
      break;
    case "DENIED":
      message = "Your order has been rejected by your manager.";
      break;
    case "NEW":
    case "PENDING_APPROVAL":
      message = "Your order requires approval. We will notify you once the request gets approved or rejected.";
      break;
    case "PROCESSING":
      message = "Your order is being processed";
      break;
    default:
      break;
  }

  if (message) {
    innerContent.push(
      <Text fontSize="T200" key={`message_${index}`}>
        {message}
      </Text>
    );
  }

  return innerContent;
};
